import React, { FC, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { useVerifiedInviteTokenData } from 'components/registration/hooks/useVerifiedInviteTokenData';
import { t } from 'ttag';

import { RecoverPassFormFields } from './types';

import Alert from '../../ui/Alert/Alert';
import BackButton from '../../ui/BackButton/BackButton';
import Button from '../../ui/button/button';
import OutlinedInput from '../../ui/input/OutlinedInput/OutlinedInput';

import { getLocationQuery, stringifyLocationQuery } from '../../../lib/url';
import auth from '../../../services/auth';
import FlexBox from '../../FlexBox/FlexBox';
import PageTitle from '../../pageTitle/pageTitle';
import { recoverPassFormResolver } from './validation';

import partnersLogo from '../../../images/auth/partners-mobile.png';

import css from './NewRecoverPassForm.css';

const NewRecoverPassForm: FC = () => {
    const [commonErrorMessage, setCommonErrorMessage] = useState<string>('');

    const history = useHistory();
    const { token = '', ...searchQuery } = getLocationQuery(history.location);
    const { email = '' } = useVerifiedInviteTokenData();

    const { control, handleSubmit, formState } = useForm<RecoverPassFormFields>(
        {
            resolver: recoverPassFormResolver,
            shouldUnregister: false,
            defaultValues: {
                email,
                password: '',
            },
        },
    );

    const onSubmit: SubmitHandler<RecoverPassFormFields> = async ({
        password,
    }) => {
        try {
            setCommonErrorMessage('');
            await auth.recoverPass({ password, token });
        } catch (err) {
            const passwordError = err.details?.password;
            const tokenError = err.details?.token && t`Невалідний токен`;
            const errorMessage = passwordError || tokenError || err.message;
            setCommonErrorMessage(errorMessage);
        }
    };

    const onBackHandler = () => {
        history.push({
            pathname: '/auth/check-email',
            search: stringifyLocationQuery(searchQuery),
        });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <PageTitle>{t`Новий пароль`}</PageTitle>
            <FlexBox className={css.container} justify="center" gap={0}>
                <div className={css.content}>
                    <FlexBox className={css.header} direction="column" gap={27}>
                        <BackButton
                            className={css.backBtn}
                            onClick={onBackHandler}
                        />
                        <img
                            src={partnersLogo}
                            className={css.partnersLogos}
                            alt=""
                        />
                        <FlexBox direction="column" gap={10}>
                            <h1 className={css.title}>{t`Новий пароль`}</h1>
                            <h4
                                className={css.description}
                            >{t`Пароль повинен містити щонайменше 8 символів, хоча б одну цифру і один символ ( ! ' # $ % * - / : ; = ? )`}</h4>
                        </FlexBox>
                    </FlexBox>
                    <Controller
                        control={control}
                        name="password"
                        render={({ field, fieldState }) => (
                            <OutlinedInput
                                type="password"
                                label={t`Пароль`}
                                value={field.value}
                                onChange={(event) =>
                                    field.onChange(event.target.value.trim())
                                }
                                autoFocus
                                error={fieldState.error?.message}
                            />
                        )}
                    />
                    {commonErrorMessage && (
                        <Alert theme="error" hideIcon>
                            {commonErrorMessage}
                        </Alert>
                    )}
                    <Button
                        className={css.submitBtn}
                        type="submit"
                        theme="darkGray"
                        disabled={formState.isSubmitting}
                    >{t`Зберегти`}</Button>
                </div>
            </FlexBox>
        </form>
    );
};

export default NewRecoverPassForm;
